import React, { useState, useEffect, useRef } from "react";
import {
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Checkbox,
} from "@mui/material";
import { AdvancedFilters } from "../../types/filters";
import FormHelperText from "@mui/material/FormHelperText";

interface SelectDropdownProps {
  label: string;
  key: string;
  filterKey: string;
  options: { key: number | string; value: string; label?: string }[];
  onChange: (selected: string | string[]) => void;
  multiple?: boolean;
  isError?: boolean;
  value?: string | string[] | undefined; // <-- allow controlled value from parent
  filters?: AdvancedFilters;
  disabled: boolean;
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  label,
  key,
  filterKey,
  options,
  onChange,
  multiple = false,
  value,
  filters,
  isError = false,
  disabled = false,
}) => {
  const initialValue =
    multiple && typeof value === "string" ? value.split(",") : value;

  const [selectedValue, setSelectedValue] = useState<
    string | string[] | undefined
  >(multiple ? initialValue || [] : value);

  const didMount = useRef(false);
  const userUnselectedAll = useRef(false);

  useEffect(() => {
    if (multiple) {
      // Always sync selectedValue with value and options
      let newSelected: string[] = [];
      if (typeof value === "string") {
        newSelected = value
          .split(",")
          .filter((v) =>
            options.some((option) => String(option.key) === String(v))
          );
      } else if (Array.isArray(value)) {
        newSelected = value.filter((v) =>
          options.some((option) => String(option.key) === String(v))
        );
      }
      // If nothing selected and options exist, select all by default
      if (
        newSelected.length === 0 &&
        options.length > 0 &&
        !userUnselectedAll.current
      ) {
        newSelected = options.map((opt) => String(opt.key));
        setSelectedValue(newSelected);
        onChange(newSelected);
      } else {
        setSelectedValue(newSelected);
      }
      // Reset the flag if user selects anything else
      if (newSelected.length > 0) {
        userUnselectedAll.current = false;
      }
    } else {
      setSelectedValue(value);
    }
    // eslint-disable-next-line
  }, [options, value]);

  useEffect(() => {
    if (
      multiple &&
      options.length > 0 &&
      (selectedValue === undefined ||
        (Array.isArray(selectedValue) && selectedValue.length === 0)) &&
      !didMount.current
    ) {
      const allKeys = options.map((opt) => String(opt.key));
      setSelectedValue(allKeys);
      onChange(allKeys);
      didMount.current = true;
    }
    // eslint-disable-next-line
  }, [options]);

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const newValue = event.target.value;
    // Prevent "__all__" from being set as a value
    if (Array.isArray(newValue) && newValue.includes("__all__")) {
      return;
    }
    // Update local state
    setSelectedValue(newValue);

    // For multiple select, convert array to comma-separated string before sending to parent
    if (multiple && Array.isArray(newValue)) {
      onChange(newValue.join(","));
    } else {
      onChange(newValue);
    }
  };

  return (
    <FormControl
      size="small"
      sx={{ minWidth: 110 }}
      variant="outlined"
      error={isError}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={multiple ? selectedValue : value}
        onChange={handleChange}
        multiple={multiple}
        disabled={disabled}
        renderValue={(selected) => {
          if (multiple) {
            const optionKeys = options.map((opt) => String(opt.key));
            const selectedArr = (selected as string[]).filter((val) =>
              optionKeys.includes(String(val))
            );
            // const selectedArr = selected as string[];
            if (selectedArr.length === options.length) {
              return "All Selected";
            }
            if (selectedArr.length > 1) {
              return `${selectedArr.length} Selected`;
            }
            return selectedArr
              .map(
                (val) =>
                  options.find((option) => String(option.key) === String(val))
                    ?.value || val
              )
              .join(", ");
          }
          return (
            options.find((option) => String(option.key) === String(selected))
              ?.value || ""
          );
        }}
        sx={{
          borderRadius: "8px",
          fontSize: "11px",
          background: "white",
          maxHeight: 25, // Optional: force the box to stay short
          overflow: "hidden",
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              maxWidth: 200, // or any value you prefer for the dropdown menu
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top", // This is correct, but see below!
            horizontal: "left",
          },
        }}
      >
        {/* <MenuItem value="" sx={{ height: '16px', py: 0, display: 'flex', justifyContent: 'flex-start', pl: 1 }}>Select</MenuItem> */}
        {multiple && options.length > 0 && (
          <MenuItem
            value="__all__"
            sx={{
              height: "32px",
              py: 0,
              display: "flex",
              justifyContent: "flex-start",
              pl: 1,
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (
                Array.isArray(selectedValue) &&
                selectedValue.length === options.length
              ) {
                setSelectedValue([]);
                onChange([]);
                userUnselectedAll.current = true;
              } else {
                const allKeys = options.map((opt) => String(opt.key));
                setSelectedValue(allKeys);
                onChange(allKeys);
                userUnselectedAll.current = false;
              }
            }}
            // Prevent MUI from selecting/deselecting "__all__" as a value
            selected={false}
          >
            <Checkbox
              checked={
                Array.isArray(selectedValue) &&
                selectedValue.length === options.length
              }
              indeterminate={
                Array.isArray(selectedValue) &&
                selectedValue.length > 0 &&
                selectedValue.length < options.length
              }
            />
            Select All
          </MenuItem>
        )}
        {options.length > 0 ? (
          options.map((option: any) => {
            return (
              <MenuItem
                key={String(option.key)}
                value={String(option.key)}
                sx={{
                  height: "32px",
                  py: 0,
                  display: "flex",
                  justifyContent: "flex-start",
                  pl: 1,
                }}
              >
                {multiple && (
                  <Checkbox
                    checked={
                      Array.isArray(selectedValue) &&
                      selectedValue.indexOf(String(option.key)) > -1
                    }
                  />
                )}
                {option.value}
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>No options available</MenuItem>
        )}
      </Select>
      <span style={{ height: "5px" }}>
        {isError && <FormHelperText>Required</FormHelperText>}
      </span>
    </FormControl>
  );
};

export default SelectDropdown;
